/**
 * AdoraCare -- IST wall-clock -> UTC conversion (pure).
 *
 * S2-P2C-APPOINTMENT-SCHEDULING-WORKBENCH-01. A clinic appointment time is a
 * wall-clock time in India Standard Time (Asia/Kolkata, a FIXED +5:30 offset
 * with no DST). A `<input type="datetime-local">` yields a naive
 * "YYYY-MM-DDTHH:mm" string with NO timezone -- interpreting it in the browser's
 * local timezone (e.g. the founder's US browser) would send the wrong instant.
 *
 * This helper treats the input as IST WALL-CLOCK and converts it to a UTC ISO
 * string with a trailing `Z`. Pure + DETERMINISTIC: it uses `Date.UTC` epoch
 * arithmetic only (no local `Date` construction), so the output NEVER depends on
 * the host machine's timezone.
 *
 *   istWallClockToUtcIso('2026-06-24T16:00') === '2026-06-24T10:30:00.000Z'
 *   (16:00 IST - 5:30 = 10:30 UTC, same machine timezone or not.)
 *
 * DOCTOR-HOME-STATE-MODEL-V1 (F6a, founder walk 2026-08-14) -- THE ONE
 * FORMATTER OWNER. This module is the frontend's ONE IST owner; the display
 * formatters below share ONE parse, and the double-offset defect class is
 * retired at that parse: a timestamp WITHOUT a zone marker is IST WALL-CLOCK
 * (this module's own input convention -- istWallClockToUtcIso has always read
 * naive as IST), NEVER "already UTC". The previous naive-means-UTC reading
 * added +5:30 to values that were already IST, rendering a ~8:40 PM close as
 * "2:10 AM IST" the next day (the founder-walked defect; every wire
 * timestamp from PostgREST carries Z or an explicit offset and is untouched
 * by this change). formatTimestampIst -- previously a byte-duplicated copy in
 * Lead360Panel.tsx with the same defect -- now lives HERE and is re-exported
 * from its old home, so all 21 consumers heal at one root. The ONE duration
 * grammar (formatDurationHm) also lives here: "38m" / "9h 38m" / "3d" --
 * never a raw "578m" (the banked ClinicDayBoard/leakage ladder, hoisted).
 *
 * Mirrors the `IST_OFFSET_MINUTES = 330` idiom used by the display-direction
 * `formatTimestampIst` (now below); the two directions must stay in lockstep
 * on the offset constant. CONFIGURABILITY SEAM (recorded, unchanged): this
 * module is deliberately IST-fixed because appointment wall-clock times are
 * IST by tenant contract today; the true clinic-local multi-tenant contract
 * is formatClinicLocalDate/DateTime (frontend/src/lib/age.ts) over
 * clinics.timezone.
 */

/** Asia/Kolkata is a fixed +5:30 offset with no DST -- safe to treat as a constant. */
export const IST_OFFSET_MINUTES = 330;

/**
 * Convert an IST wall-clock `datetime-local` value ("YYYY-MM-DDTHH:mm", optional
 * ":ss") to a UTC ISO-8601 string with a trailing `Z`. Throws on a value that is
 * not in the expected naive shape (the caller validates the input first). NEVER
 * uses the browser timezone.
 */
export function istWallClockToUtcIso(local: string): string {
  const m = local
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) {
    throw new Error(
      'istWallClockToUtcIso: expected a naive "YYYY-MM-DDTHH:mm" datetime-local value',
    );
  }
  const [, y, mo, d, hh, mm, ss] = m;
  // The naive wall-clock fields are IST; the true UTC instant is 5:30 earlier.
  const utcMs =
    Date.UTC(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss ?? '0'),
    ) -
    IST_OFFSET_MINUTES * 60_000;
  return new Date(utcMs).toISOString();
}

/** True for a non-empty naive datetime-local value of the expected shape. */
export function isWallClockValue(local: string): boolean {
  return /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.test(
    local.trim(),
  );
}

/**
 * THE ONE ISO PARSE (F6a): every display formatter below reads through this.
 * Accepts a UTC `Z` suffix, an explicit `+HH:MM` / `-HH:MM` offset (with
 * optional fractional seconds), or a bare naive value -- which is IST
 * WALL-CLOCK by this module's own convention (the double-offset fix; see the
 * module header). Returns a Date whose UTC getters read the IST wall-clock
 * fields, or null for an unparseable value so each caller keeps its own
 * fallback contract.
 */
function istWallClockDate(iso: string): Date | null {
  if (!iso) return null;
  const m = iso
    .trim()
    .match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/,
    );
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss, zone] = m;
  let fieldMs = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(hh),
    Number(mm),
    Number(ss ?? '0'),
  );
  if (zone === undefined) {
    // NAIVE = IST WALL-CLOCK already (istWallClockToUtcIso's own input
    // convention). Treating it as UTC double-shifted an already-IST value --
    // the "2:10 AM IST for a ~8:40 PM close" founder-walked defect class.
    return new Date(fieldMs);
  }
  if (zone !== 'Z') {
    // Normalise an explicit offset back to the true UTC instant first.
    const sign = zone[0] === '-' ? -1 : 1;
    const z = zone.slice(1).replace(':', '');
    fieldMs -=
      sign * (Number(z.slice(0, 2)) * 60 + Number(z.slice(2, 4))) * 60000;
  }
  return new Date(fieldMs + IST_OFFSET_MINUTES * 60000);
}

/**
 * Format an ISO timestamp as a clear 12-hour IST wall-clock TIME, e.g.
 * "1:45 PM". Returns '' for an unparseable value so the caller can fall back.
 *
 * Pure + DETERMINISTIC: epoch arithmetic via the ONE parse above -- a US
 * browser shows the clinic's IST time, NOT the host machine's local time.
 * The display-direction companion to istWallClockToUtcIso (same IST offset),
 * for compact lists that show only the clock time.
 */
export function formatIstTime(iso: string): string {
  const ist = istWallClockDate(iso);
  if (ist === null) return '';
  let h = ist.getUTCHours();
  const min = String(ist.getUTCMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

/**
 * APPOINTMENT-BOOK-V1: an ISO instant -> IST MINUTES OF DAY (0..1439), or null
 * for an unparseable value. The band-placement companion to formatIstTime --
 * the planning calendar needs the numeric position of a row inside the clinic
 * day, and this module is the frontend's ONE IST owner, so it lives here
 * instead of a second offset arithmetic in the calendar view.
 *
 * The backend's istMinutesOfDay (lib/domain/appointment/booking-rules.ts) is
 * the server-side sibling; both read the same fixed +05:30 offset.
 */
export function istMinutesOfDay(iso: string): number | null {
  const ist = istWallClockDate(iso);
  if (ist === null) return null;
  return ist.getUTCHours() * 60 + ist.getUTCMinutes();
}

/**
 * FOLLOWUP-DUE-DATE-PLUMBING-V1: an epoch instant -> the IST CALENDAR DAY it
 * falls on, as a bare "YYYY-MM-DD". The clinic day is the IST business day
 * (fixed +5:30, no DST), so a due-date comparison must never use the
 * browser's local calendar -- at 7:30 PM UTC it is already tomorrow in the
 * clinic. Lives HERE because this module is the frontend's ONE IST owner
 * (the server-side sibling of this shape is lib/billing-queue.ts's IST day
 * arithmetic). Pure epoch arithmetic; deterministic on any host.
 */
export function istCalendarDayYmd(ms: number): string {
  return new Date(ms + IST_OFFSET_MINUTES * 60_000).toISOString().slice(0, 10);
}

/** Month abbreviations for the compact IST date+time formatters (ASCII only). */
const IST_MONTHS: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// NOTE (PATIENT-360-AND-JOURNEY-TRUTH-CORRECTIVE-V1 timezone corrective):
// a fixed-IST `formatIstDate` was briefly added here for the registered-date
// contract and REMOVED -- an IST-fixed formatter must NEVER be described as
// generic clinic-local multi-tenant behavior. The registered-date contract is
// the TRUE clinic-local `formatClinicLocalDate` (frontend/src/lib/age.ts),
// which formats through the clinic's IANA `clinics.timezone` (DST-safe via
// Intl). This datetime-ist module stays deliberately IST-fixed: its formatters
// render appointment WALL-CLOCK times, which are IST by tenant contract today.

/**
 * Format an ISO timestamp as a compact 12-hour IST DATE + TIME, e.g.
 * "5 Jul 2026, 1:45 PM". Same deterministic epoch arithmetic as formatIstTime
 * (a US browser shows the clinic's IST time, never the host's local time).
 * Returns '' for an unparseable value so the caller can fall back. The date+time
 * companion to formatIstTime, for ops timelines that need the day AND a 12-hour
 * clock (LEAD-APPOINTMENT-CRM-COHERENCE-V1 SCOPE 6 - 12-hour sweep).
 */
export function formatIstDateTime(iso: string): string {
  const ist = istWallClockDate(iso);
  if (ist === null) return '';
  const day = ist.getUTCDate();
  const mon = IST_MONTHS[ist.getUTCMonth()];
  const year = ist.getUTCFullYear();
  let h = ist.getUTCHours();
  const min = String(ist.getUTCMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${day} ${mon} ${year}, ${h}:${min} ${ampm}`;
}

/**
 * LEAD-360 timeline grammar, relocated (DOCTOR-HOME-STATE-MODEL-V1 F6a): an
 * ISO timestamp as a human-readable IST string WITH the zone shown, e.g.
 * "8 Jun 2026, 9:58 PM IST". Previously a byte-duplicated copy in
 * Lead360Panel.tsx (which now re-exports THIS one -- zero call-site churn for
 * its 21 consumers, the day-scope re-export precedent); the duplication is
 * exactly the LES-126 second-formatter class, retired here. Falls back to
 * the trimmed input for any non-ISO value (e.g. "Not captured") so a
 * malformed / absent timestamp degrades gracefully rather than leaking a raw
 * ISO string with microseconds.
 */
export function formatTimestampIst(iso: string): string {
  if (!iso) return '';
  const ist = istWallClockDate(iso);
  if (ist === null) return iso.trim();
  const month = IST_MONTHS[ist.getUTCMonth()] ?? String(ist.getUTCMonth() + 1);
  let h = ist.getUTCHours();
  const min = String(ist.getUTCMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${ist.getUTCDate()} ${month} ${ist.getUTCFullYear()}, ${h}:${min} ${ampm} IST`;
}

/** Date-only financial paper grammar; the stored instant stays unchanged.
 * Uses the same IST day boundary as the in-app timestamp, including reprints.
 * Invalid values are empty so a caller can show an honest missing label. */
export function formatIstDate(iso: string): string {
  const ist = istWallClockDate(iso);
  if (ist === null) return '';
  return `${ist.getUTCDate()} ${IST_MONTHS[ist.getUTCMonth()]} ${ist.getUTCFullYear()}`;
}

/**
 * DOCTOR-CONSULT-DEMO-POLISH-V1 (item 2, ONE CLOCK): the compact IST
 * DAY + MONTH render, e.g. "11 Aug" -- THE ONE owner for that short form
 * (extends this module in place; never a second date formatter for the same
 * shape). Same deterministic IST epoch arithmetic as its siblings ('' for an
 * unparseable value), so every "short date" on a consult surface -- the
 * visit date, the next-appointment date, the journey-opened date -- reads
 * off the SAME clock instead of drifting near the IST day boundary the way
 * a naive UTC-substring read would. Accepts a full ISO timestamp OR a bare
 * YYYY-MM-DD business date (the formatIstDateDdMmYyyy precedent, extended in
 * place for FOLLOWUP-DUE-DATE-PLUMBING-V1's DATE-column values -- a calendar
 * day needs no offset arithmetic at all).
 */
export function formatIstShortDate(iso: string): string {
  if (!iso) return '';
  const bare = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (bare) {
    const month = IST_MONTHS[Number(bare[2]) - 1];
    if (month === undefined) return '';
    return `${Number(bare[3])} ${month}`;
  }
  const ist = istWallClockDate(iso);
  if (ist === null) return '';
  return `${ist.getUTCDate()} ${IST_MONTHS[ist.getUTCMonth()]}`;
}

/**
 * RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-33): the Indian-locale DATE-ONLY
 * render, dd/mm/yyyy -- THE ONE owner for that format (extends this module
 * in place; never a second date formatter). Same deterministic IST epoch
 * arithmetic as its siblings; '' for an unparseable value. Accepts a full
 * ISO timestamp OR a bare YYYY-MM-DD business date.
 */
export function formatIstDateDdMmYyyy(iso: string): string {
  if (!iso) return '';
  const bare = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (bare) {
    const [, y, mo, d] = bare;
    return `${d}/${mo}/${y}`;
  }
  const ist = istWallClockDate(iso);
  if (ist === null) return '';
  const dd = String(ist.getUTCDate()).padStart(2, '0');
  const mm2 = String(ist.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm2}/${ist.getUTCFullYear()}`;
}

/**
 * AUG-12-WALK-BLOCKERS-V1 (F6): the INVERSE of formatIstDateDdMmYyyy --
 * parse a typed dd/mm/yyyy date to the wire's bare YYYY-MM-DD, THE ONE
 * owner beside its formatter (never a second date grammar). Strict:
 * dd/mm/yyyy digits only (separators / or -), a REAL calendar date
 * (30/02 rejected), year 1900..2099. Null for anything else -- the caller
 * treats null as "not a date yet", never a silent reinterpretation
 * (mm/dd is structurally impossible here: the first segment is the day).
 */
export function parseDdMmYyyyToIso(value: string): string | null {
  const m = value.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (year < 1900 || year > 2099) return null;
  if (month < 1 || month > 12) return null;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day < 1 || day > daysInMonth) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * DOCTOR-HOME-STATE-MODEL-V1 (F6a) -- THE ONE DURATION GRAMMAR: whole minutes
 * as "38m" / "9h 38m" ("9h" when the remainder is zero) / "3d" -- never a raw
 * "578m". This is the banked compact age ladder (dayBoardStageElapsedLabel,
 * ClinicDayBoard; formatLeakageAge, the leakage surface) HOISTED to the one
 * shared home; the board's own label now delegates here, and every
 * doctor-surface wait/draft clock renders through this function. Negative or
 * fractional input clamps to whole non-negative minutes (a clock skew never
 * renders "-3m").
 */
export function formatDurationHm(minutes: number): string {
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m}m`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  return `${Math.floor(m / 1440)}d`;
}
